import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Optional plain-text label used for the trigger when `label` is a node. */
  text?: string;
  disabled?: boolean;
}

/** Fixed-position box for the portalled listbox, anchored to the trigger. */
interface Anchor {
  left: number;
  width: number;
  top: number;
  bottom: number;
  /** Open below the trigger (true) or flip above it (false). */
  down: boolean;
  maxHeight: number;
}

/**
 * A design-system dropdown: a token-styled trigger + popover listbox that
 * replaces the raw `<select>`. The listbox is rendered in a portal with fixed
 * positioning, so it floats above (and is never clipped by) modals or other
 * `overflow` containers. Fully keyboard-accessible (↑/↓/Home/End to move,
 * Enter/Space to choose, Esc to close), closes on outside-click, both themes.
 */
export function Select<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
  placeholder = 'Select…',
  size = 'default',
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  size?: 'default' | 'sm';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listId = useId();

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;
    setActive(selectedIndex >= 0 ? selectedIndex : 0);

    // Anchor the listbox to the trigger; keep it glued while anything scrolls.
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const down = spaceBelow >= 260 || spaceBelow >= spaceAbove;
      const maxHeight = Math.max(140, Math.min(280, (down ? spaceBelow : spaceAbove) - 12));
      setAnchor({ left: r.left, width: r.width, top: r.top, bottom: r.bottom, down, maxHeight });
    };
    place();

    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    };
    // `capture` catches scrolls of inner containers (e.g. a modal body) too.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
    };
    // Recompute each time the popover opens.
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const move = (delta: number) => {
    setActive((i) => {
      let n = i;
      for (let step = 0; step < options.length; step++) {
        n = (n + delta + options.length) % options.length;
        if (!options[n]?.disabled) break;
      }
      return n;
    });
  };

  const choose = (i: number) => {
    const opt = options[i];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(active);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-sm border border-border bg-surface-3 text-left text-sm text-bone transition-colors',
          'focus:border-iris focus:outline-none focus:ring-2 focus:ring-iris-dim',
          'disabled:cursor-not-allowed disabled:opacity-50',
          size === 'sm' ? 'h-8 px-2.5' : 'h-9 px-3',
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted')}>
          {selected ? (selected.text ?? selected.label) : placeholder}
        </span>
        <span aria-hidden className={cn('text-muted transition-transform', open && 'rotate-180')}>
          ▾
        </span>
      </button>

      {open &&
        anchor &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              left: anchor.left,
              // Grow to the widest option so labels never truncate, but never
              // narrower than the trigger nor wider than the space to the edge.
              minWidth: anchor.width,
              width: 'max-content',
              maxWidth: Math.max(anchor.width, window.innerWidth - anchor.left - 12),
              maxHeight: anchor.maxHeight,
              ...(anchor.down
                ? { top: anchor.bottom + 4 }
                : { bottom: window.innerHeight - anchor.top + 4 }),
            }}
            className="z-[70] overflow-auto rounded-md border border-hairline bg-surface py-1 shadow-pop"
          >
            {options.map((opt, i) => {
              const isSelected = opt.value === value;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(i);
                  }}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 text-sm transition-colors',
                    opt.disabled && 'cursor-not-allowed opacity-40',
                    i === active ? 'bg-surface-2 text-bone' : 'text-haze',
                  )}
                >
                  <span className="whitespace-nowrap">{opt.label}</span>
                  {isSelected && (
                    <span aria-hidden className="text-iris">
                      ✓
                    </span>
                  )}
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
